import React, { useState } from 'react';
import { ShieldCheck, CheckCircle, XCircle, Search, ExternalLink, Clock, Award, User, Hash } from '../components/CustomIcons';
import { useAuth } from '../context/AuthContext';
import LogoImg from '../components/LogoImg';

export default function Verify() {
  const { API } = useAuth() || { API: `${process.env.REACT_APP_BACKEND_URL}/api` };
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API}/credentials/verify/${code.trim().toUpperCase()}`);
      const data = await res.json();
      setResult(data);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg-deep)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', fontFamily: 'Inter, sans-serif' }} data-testid="verify-page">
      {/* Nav */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, background: 'rgba(13,13,13,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1E1E1E', padding: '0 24px', height: '60px', display: 'flex', alignItems: 'center' }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <LogoImg forceDark height={30} alt="Assistiv OS" style={{ display: 'block' }} />
        </a>
        <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-dim)' }}>/ Credential Verification</span>
      </div>

      <div style={{ width: '100%', maxWidth: '540px', marginTop: '40px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(249,115,22,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <ShieldCheck size={32} color="#F97316" />
          </div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '32px', color: 'var(--text-primary)', margin: '0 0 12px' }}>
            Verify a Credential
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px', margin: 0 }}>
            All AssistivOS credentials are independently verified. Enter a verification code to confirm authenticity.
          </p>
        </div>

        {/* Verify Input */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '16px', padding: '32px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: '#888888', fontWeight: 600, marginBottom: '10px', letterSpacing: '0.5px' }}>
            VERIFICATION CODE
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              placeholder="e.g. AOS-ELEC-7842-X"
              style={{ flex: 1, background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '13px 16px', color: 'var(--text-primary)', fontSize: '16px', outline: 'none', letterSpacing: '1px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}
              data-testid="verify-code-input"
            />
            <button
              onClick={handleVerify}
              disabled={loading || !code.trim()}
              style={{ background: code.trim() ? '#F97316' : '#222222', border: 'none', borderRadius: '10px', padding: '13px 20px', cursor: code.trim() ? 'pointer' : 'default', color: code.trim() ? '#111111' : '#444444', fontWeight: 700, fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s', minWidth: '100px', justifyContent: 'center' }}
              data-testid="verify-submit-btn">
              {loading ? <div style={{ width: '16px', height: '16px', border: '2px solid #111111', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <><Search size={14} /> Verify</>}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: '14px', padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <XCircle size={16} color="#EF4444" />
              <span style={{ fontSize: '13px', color: '#EF4444' }}>{error}</span>
            </div>
          )}

          {/* Result */}
          {result && (
            <div style={{ marginTop: '20px' }}>
              {result.verified ? (
                <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '12px', padding: '20px' }} data-testid="verify-success">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <CheckCircle size={24} color="#22C55E" />
                    <div>
                      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: '#22C55E' }}>Credential Verified</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{result.message}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { icon: User, label: 'Learner', value: result.credential.learner_name },
                      { icon: Award, label: 'Credential', value: result.credential.title },
                      { icon: ShieldCheck, label: 'Type', value: result.credential.type },
                      { icon: Hash, label: 'Issued By', value: result.credential.issued_by },
                      { icon: Clock, label: 'Date Earned', value: result.credential.date_earned ? new Date(result.credential.date_earned).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A' },
                    ].filter(f => f.value).map(field => (
                      <div key={field.label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <field.icon size={14} color="#555555" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginBottom: '2px' }}>{field.label}</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>{field.value}</div>
                        </div>
                      </div>
                    ))}
                    {result.credential.competencies?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginBottom: '8px' }}>COMPETENCIES</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {result.credential.competencies.map(c => (
                            <span key={c} style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#22C55E', fontSize: '12px', padding: '3px 10px', borderRadius: '6px' }}>{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ marginTop: '4px', padding: '10px 14px', background: 'rgba(34,197,94,0.06)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ShieldCheck size={14} color="#22C55E" />
                      <span style={{ fontSize: '12px', color: '#22C55E' }}>Verification Code: <strong>{result.credential.verification_code}</strong></span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px', padding: '20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }} data-testid="verify-fail">
                  <XCircle size={24} color="#EF4444" style={{ flexShrink: 0 }} />
                  <div>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: '#EF4444', marginBottom: '6px' }}>Not Verified</div>
                    <p style={{ fontSize: '13px', color: '#888888', margin: 0 }}>{result.message}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div style={{ textAlign: 'center', marginTop: '28px' }}>
          <p style={{ fontSize: '13px', color: '#333333', lineHeight: 1.7 }}>
            Verification codes are found on the credential certificate or can be obtained directly from the certificate holder. All credentials are issued by Tech Tradesmith Institute via Assistiv OS.
          </p>
          <a href="/login" style={{ color: '#F97316', fontSize: '13px', textDecoration: 'none', fontWeight: 600 }}>
            Sign in to the platform →
          </a>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
